<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  BaseAvatar,
  BaseButton,
  BaseIcon,
  FieldCheckbox,
  FieldInput,
  FieldPhoneNumber,
  FieldSelect,
  getDisplayName,
  getInitials,
} from '@magma-app/magma-lapilli';

import type { SharingFormData } from '@/composables/helpee-sharing/useSharingForm';

type Option = { label: string; value: string };

type TouchpointOption = {
  label: string;
  value: number;
  icon: string;
  type: string;
};

const props = defineProps<{
  formData: SharingFormData;
  campaignsOptions: Option[];
  criteriaOptions: Option[];
  availableTouchpoints: TouchpointOption[];
  desiredTouchpoints: TouchpointOption[];
  strongCriteria?: {
    id: number | string;
    labelHelpee?: string;
    type?: string;
  };
  showCampaignSelect: boolean;
  showForm: boolean;
  validation: any;
  duplicateHelpeeMsg?: string;
  disableBtn: boolean;
  primaryColor?: string;
  rgbColor?: string;
  helper?: {
    firstname: string;
    lastname: string;
    profilePictureUrl?: string;
  };
}>();

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'select-campaign'): void;
  (event: 'submit'): void;
}>();

const { t, locale } = useI18n({ useScope: 'global' });
const safeRgbColor = computed(() => props.rgbColor || '');
const hasCriteria = computed(() => {
  const criteria = props.formData.signUp.criteria;

  if (Array.isArray(criteria)) {
    return criteria.length > 0;
  }

  return Boolean(criteria);
});
const hasRequiredValues = computed(() => {
  return (
    Boolean(props.formData.signUp.firstname) &&
    Boolean(props.formData.signUp.lastname) &&
    Boolean(props.formData.signUp.email) &&
    Boolean(props.formData.signUp.phoneNumber) &&
    hasCriteria.value &&
    props.formData.signUp.consent === true
  );
});
const hasValidationErrors = computed(() => {
  const validation = props.validation?.signUp;
  if (!validation) return false;

  return Boolean(
    validation.firstname?.$error ||
    validation.lastname?.$error ||
    validation.email?.$error ||
    validation.phoneNumber?.$error ||
    validation.criteria?.$error ||
    validation.consent?.$error
  );
});
const canSubmit = computed(() => {
  return (
    hasRequiredValues.value && !hasValidationErrors.value && !props.disableBtn
  );
});

const helperFullName = computed(() => {
  if (!props.helper) return '';
  return getDisplayName(props.helper.firstname, props.helper.lastname);
});

const helperFallback = computed(() => {
  if (!props.helper) return '';
  return getInitials(props.helper.firstname, props.helper.lastname);
});
</script>

<template>
  <div class="pt-4">
    <div class="relative flex border-b border-border-subtle px-5 pb-4 md:px-10">
      <BaseAvatar
        v-if="helper"
        :src="helper.profilePictureUrl"
        :alt="`${t('generic.profilePicture')} ${helperFullName}`"
        :fallback="helperFallback"
        size="sm"
      />

      <h2 class="ml-3 text-mgm-txt-sm font-medium text-foreground-emphasis">
        {{ helperFullName }}
      </h2>

      <button
        class="absolute right-5 top-0.5 cursor-pointer md:right-10"
        @click="emit('close')"
      >
        <BaseIcon color="#5F6C85" icon="close" />
      </button>
    </div>

    <div class="px-5 pt-5 md:px-10">
      <h3 class="text-mgm-txt-lg font-medium text-foreground-emphasis">
        {{ t('helpeeSharingView.intro') }}
      </h3>

      <form @submit.prevent="emit('submit')">
        <FieldSelect
          v-if="showCampaignSelect"
          id="campaigns"
          v-model="formData.selectedCampaign"
          class="mt-4"
          :custom-color="primaryColor"
          :custom-rgb-color="safeRgbColor"
          :label="t('helpeeSharingView.campaign')"
          :options="campaignsOptions"
          :placeholder="t('generic.placeholder.singleSelect')"
          :validation="validation?.selectedCampaign"
          required
          @change="emit('select-campaign')"
        />

        <template v-if="showForm">
          <FieldInput
            id="firstname"
            v-model="formData.signUp.firstname"
            class="mt-4"
            :locale="locale"
            :label="t('generic.firstNameLabel')"
            placeholder="John"
            required
            :validation="validation?.signUp?.firstname"
            :custom-color="primaryColor"
            :custom-rgb-color="safeRgbColor"
          />

          <FieldInput
            id="lastname"
            v-model="formData.signUp.lastname"
            class="mt-4"
            :locale="locale"
            :label="t('generic.lastNameLabel')"
            placeholder="Doe"
            required
            :validation="validation?.signUp?.lastname"
            :custom-color="primaryColor"
            :custom-rgb-color="safeRgbColor"
          />

          <FieldInput
            id="email"
            v-model="formData.signUp.email"
            class="mt-4"
            role="email"
            type="email"
            :locale="locale"
            label="Email"
            :placeholder="`${t('generic.example')}@email.com`"
            autocomplete="email"
            required
            :validation="validation?.signUp?.email"
            :error-message="duplicateHelpeeMsg"
            :custom-color="primaryColor"
            :custom-rgb-color="safeRgbColor"
            icon="mail"
          />

          <FieldPhoneNumber
            id="phone"
            v-model="formData.signUp.phoneNumber"
            class="mt-4"
            :locale="locale"
            :label="t('generic.phone')"
            required
            :validation="validation?.signUp?.phoneNumber"
            :custom-color="primaryColor"
            :custom-rgb-color="safeRgbColor"
          />

          <FieldSelect
            v-if="criteriaOptions.length"
            :id="strongCriteria?.id"
            v-model="formData.signUp.criteria"
            class="mt-4"
            :custom-color="primaryColor"
            :custom-rgb-color="safeRgbColor"
            :label="strongCriteria?.labelHelpee"
            :multiple="strongCriteria?.type === 'multiSelect'"
            :options="criteriaOptions"
            :placeholder="
              strongCriteria?.type === 'multiSelect'
                ? t('generic.placeholder.multiSelect')
                : t('generic.placeholder.singleSelect')
            "
            :validation="validation?.signUp?.criteria"
            required
          />

          <FieldSelect
            id="touchpoints"
            v-model="formData.signUp.chosenTouchpoints"
            class="mt-4"
            :custom-color="primaryColor"
            :custom-rgb-color="rgbColor"
            :label="t('helpeeSharingView.contact')"
            :options="availableTouchpoints"
            :placeholder="t('generic.placeholder.multiSelect')"
            multiple
          />

          <template
            v-for="desiredTouchpoint of desiredTouchpoints"
            :key="desiredTouchpoint.value"
          >
            <FieldPhoneNumber
              v-if="desiredTouchpoint.type === 'phoneNumber'"
              :id="desiredTouchpoint.value"
              v-model="formData.signUp.touchpoints[desiredTouchpoint.label]"
              class="mt-4"
              :locale="locale"
              placeholder="06 06 06 06 06"
              :validation="
                validation?.signUp?.touchpoints?.[desiredTouchpoint.label]
              "
              :custom-color="primaryColor"
              :custom-rgb-color="safeRgbColor"
            />

            <FieldInput
              v-else
              :id="desiredTouchpoint.value"
              v-model="formData.signUp.touchpoints[desiredTouchpoint.label]"
              class="mt-4"
              :role="desiredTouchpoint.type === 'email' ? 'email' : 'default'"
              :type="desiredTouchpoint.type === 'email' ? 'email' : 'url'"
              :locale="locale"
              :placeholder="
                desiredTouchpoint.type === 'email'
                  ? `${t('generic.example')}@email.com`
                  : `https://www.${t('generic.example')}.com/${t(
                      'generic.example'
                    )}`
              "
              :touchpoint="desiredTouchpoint.icon"
              :validation="
                validation?.signUp?.touchpoints?.[desiredTouchpoint.label]
              "
              :custom-color="primaryColor"
              :custom-rgb-color="safeRgbColor"
              :custom-icon="desiredTouchpoint.icon"
            />
          </template>

          <FieldCheckbox
            id="consent"
            v-model="formData.signUp.consent"
            class="mt-8"
            :custom-color="primaryColor"
            :custom-rgb-color="safeRgbColor"
            :validation="validation?.signUp?.consent"
            required
          >
            <span class="text-mgm-txt-sm">
              {{ t('generic.agree') }}

              <a
                class="font-medium text-foreground-brand-default"
                href="https://www.magma.app/legals/helpers-terms-of-use"
                target="_blank"
              >
                {{ t('generic.termsAndPrivacy.part2') }}
              </a>

              {{ t('generic.termsAndPrivacy.part3') }}

              <a
                class="font-medium text-foreground-brand-default"
                href="https://www.magma.app/legals/privacy-policy"
                target="_blank"
              >
                {{ t('generic.termsAndPrivacy.part4') }}
              </a>
            </span>
          </FieldCheckbox>
        </template>

        <BaseButton
          class="mt-8 w-full"
          :custom-color="primaryColor"
          :custom-rgb-color="safeRgbColor"
          :disabled="!canSubmit"
          :text="t('helpeeSharingView.send')"
          size="md"
          type="submit"
        />
      </form>
    </div>
  </div>
</template>
